import * as React from 'react';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { Button, Row, Col, Typography, Table } from 'antd';
const { Title } = Typography;

import { FormikForm } from 'client/ui/form/FormikForm';
import { SelectInputItem } from 'client/ui/form/input/SelectInputItem';
import { TextInputItem } from 'client/ui/form/input/TextInputItem';
import { ProcedureTableCellRecipient } from 'client/components/schema/procedures/table/ProcedureTableCellRecipient';
import { ProcedureFrontTableTitle } from 'client/components/schema/procedures/table/ProcedureFrontTableTitle';
import { ProcedureFrontTableActions } from 'client/components/schema/procedures/table/ProcedureFrontTableActions';
import {
  IProcedure,
  ProcedureRecipient,
  ProcedureState
} from 'common/schema/Procedure';
import { ColumnProps } from 'antd/lib/table';
import { useApiQuery } from 'client/core/network/hooks/useApiQuery';
import { ProcedureApi } from 'client/components/schema/procedures/ProcedureApi';
import { useState } from 'react';
import { IProceduresQueryDto } from 'common/dto/ProcedureDto';
import { CheckboxInput } from 'client/ui/form/input/CheckboxInput';
import { Formik } from 'formik';
import { tableLoaderSpin } from 'client/ui/loader/tableLoaderSpin';
import { TopFilterColButton } from 'client/ui/filter-panel/TopFilterColButton';
import { AlertPage } from 'client/ui/alert/AlertPage';
import { getNetworkErrorMessage } from 'client/core/network/errors/getNetworkErrorMessage';
import { useConfigRecipientEnabled } from 'client/components/schema/procedures/hooks/useConfigRecipientEnabled';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { RouteComponentProps } from 'react-router-dom';
import { CollapsablePanel } from 'client/ui/filter-panel/CollapsablePanel';
import { ProcedureCategorySelectInput } from 'client/components/schema/procedures/filter/ProcedureCategorySelectInput';
import { useThemeLabel } from 'client/components/schema/config/ClientConfigModule';
import { useTranslation } from 'react-i18next';
import { ProcedureAdministrationCodesFilter } from 'client/components/schema/procedures/filter/ProcedureAdministrationCodesFilter';

export interface ProceduresListViewPageProps extends RouteComponentProps {}

export function ProceduresListViewPage(props: ProceduresListViewPageProps) {
  const [query, setQuery] = useState<IProceduresQueryDto>({
    page: 1,
    isActive: false,
    pageSize: 10,
    isPublished: true
  });
  const { response, loading, error } = useApiQuery(ProcedureApi.frontend.list, {
    data: { query }
  });

  const recipientEnabled = useConfigRecipientEnabled();
  const organizationName = useThemeLabel('organizationName');
  const { t } = useTranslation();

  useBreadcrumbItem({
    path: props.location.pathname,
    title: t('procedures.breadcrumb', 'Istanze'),
    menuKey: 'domande'
  });

  const columns: ColumnProps<IProcedure>[] = [
    {
      title: t('procedures.list.recipients', 'Tipologia'),
      dataIndex: 'recipients',
      key: 'recipients',
      responsive: ['md'],
      width: 54,
      render: (_, record) => (
        <ProcedureTableCellRecipient recipient={record.recipient} />
      )
    },
    {
      title: t('procedures.list.name', 'Domanda'),
      dataIndex: 'title',
      key: 'title',
      render: (_, record) => <ProcedureFrontTableTitle procedure={record} />
    },
    {
      title: t('procedures.list.actions', 'Azioni'),
      key: 'actions',
      responsive: ['md'],
      width: 110,
      render: (_, record) => (
        <ProcedureFrontTableActions to={`/domande/${record.id}`} />
      )
    }
  ];

  if (error)
    return (
      <AlertPage
        showIcon
        type="error"
        message={t(
          'procedures.list.error-loading',
          "Impossibile caricare l'elenco delle istanze"
        )}
        description={getNetworkErrorMessage(error)}
      />
    );

  return (
    <FrontLayout>
      <Title level={3}>{t('procedures.list.title', 'Tutte le Istanze')}</Title>

      <CollapsablePanel
        key="filter"
        header={t('procedures.list.filter-procedures', 'Filtra Istanze')}
      >
        <Formik
          enableReinitialize
          initialValues={query}
          onSubmit={values => {
            setQuery({ ...query, ...values });
          }}
        >
          <FormikForm layout="vertical">
            <Row gutter={8} align="bottom">
              {recipientEnabled && false && (
                <Col span={24} md={4}>
                  <SelectInputItem
                    name="recipient"
                    label="Destinatari"
                    placeholder="Destinario..."
                    options={
                      [
                        {
                          value: ProcedureRecipient.Citizen,
                          label: 'Persona Fisica'
                        },
                        {
                          value: ProcedureRecipient.Company,
                          label: 'Persona Giuridica'
                        },
                        {
                          value: ProcedureRecipient.Employee,
                          label: `Dipendenti ${organizationName}`
                        }
                      ] as any
                    }
                  />
                </Col>
              )}
              {/* Scelta AOO (Amministrazione) se configurato multi amministrazione */}
              <ProcedureAdministrationCodesFilter />
              <Col span={24} md={4}>
                <ProcedureCategorySelectInput
                  name="category"
                  label={t('procedures.category.label', 'Categoria')}
                  allowClear
                  placeholder={t(
                    'procedures.category.placeholder',
                    'Categoria..'
                  )}
                />
              </Col>
              <Col flex="auto">
                <TextInputItem
                  name="title"
                  allowClear
                  label={t('procedures.title.label', 'Titolo')}
                  placeholder={t(
                    'procedures.title.placeholder',
                    'Inserisci le parole da cercare'
                  )}
                />
              </Col>
              <Col span={24} md={4}>
                <SelectInputItem
                  name="state"
                  label={t('procedures.state-citizen.label', 'Stato')}
                  placeholder={t(
                    'procedures.state-citizen.placeholder',
                    'Cerca per stato..'
                  )}
                  options={
                    [
                      {
                        value: undefined,
                        label: t('procedures.state.plural.every', 'Tutte')
                      },
                      {
                        value: ProcedureState.Published,
                        label: t(
                          'procedures.state.plural.published',
                          'In Corso'
                        )
                      },
                      {
                        value: ProcedureState.Active,
                        label: t('procedures.state.plural.active', 'Aperte')
                      },
                      {
                        value: ProcedureState.Evaluating,
                        label: t(
                          'procedures.state.plural.evaluating',
                          'In Corso'
                        )
                      },
                      {
                        value: ProcedureState.Expired,
                        label: t('procedures.state.plural.expired', 'Chiuse')
                      }
                    ] as any
                  }
                />
              </Col>
              {/* <TopFilterColButton /> Elemento <Col /> dedicato per stili css */}
              <TopFilterColButton>
                {/* <CheckboxInput name="isActive" children="Solo Attive" /> */}
                <Button type="primary" htmlType="submit">
                  {t('procedures.filter-action.label', 'Filtra')}
                </Button>
              </TopFilterColButton>
            </Row>
          </FormikForm>
        </Formik>
      </CollapsablePanel>
      {/* Lista Domande */}
      <Table
        loading={tableLoaderSpin(loading || !response?.data)}
        tableLayout="fixed"
        showHeader={false}
        size="small"
        rowKey="id"
        dataSource={response?.data.items ?? []}
        columns={columns}
        onChange={(pagination, filters, sorter) => {
          setQuery({
            ...query,
            page: pagination.current || query.page,
            pageSize: pagination.pageSize || query.pageSize
          });
        }}
        pagination={{
          size: 'small',
          position: ['bottomRight'],
          showSizeChanger: true,
          total: response?.data.meta.total,
          pageSize: query.pageSize,
          current: query.page,
          pageSizeOptions: ['5', '10', '20', '30', '40']
        }}
      />
    </FrontLayout>
  );
}
